// components/FieldTemplateItem/FieldTemplateItem.tsx

import { useSelector } from 'react-redux'
import classes from './FieldTemplateItem.module.scss'
import { CategoryFieldTemplateInterface } from '../../types/interfaces'
import { CategoryFieldType } from '../../types/enums'
import { IconButton } from '../iconButton/IconButton'
import { selectDarkTheme } from '../../../store/slices/settingsSlice'
import add from '../../../assets/images/icons/add.svg'
import trash from '../../../assets/images/icons/trash.svg'
import up from '../../../assets/images/icons/arrow_up.svg'
import down from '../../../assets/images/icons/down.svg'
import { maxFieldTemplatesCount, sortStep } from '../../utils/constants'
import { createTypeSelectList } from '../../utils/functions/createTypeSelectList'
import { categoryFieldTypeToText } from '../../utils/functions/enumToText'
import { Toggle } from '../newToggle/Toggle'

type Props = {
  el: CategoryFieldTemplateInterface
  length: number
  index: number
  addItem: (el: CategoryFieldTemplateInterface) => void
  removeItem: (el: CategoryFieldTemplateInterface) => void
  itemUp: (el: CategoryFieldTemplateInterface) => void
  itemDown: (el: CategoryFieldTemplateInterface) => void
  onChange: (id: string, updates: Partial<CategoryFieldTemplateInterface>) => void
}

export const FieldTemplateItem = ({
  el,
  length,
  index,
  addItem,
  removeItem,
  itemUp,
  itemDown,
  onChange,
}: Props) => {
  const dark = useSelector(selectDarkTheme)

  const isNameInvalid = !el.name || el.name.length > 50
  const isDescriptionInvalid = el.description && el.description.length > 200
  const isTypeInvalid = el.type === CategoryFieldType.NONE

  return (
    <div className={classes.container}>
      <div className={classes.fields}>
        <input
          className={`${dark ? classes.inputDark : classes.input} ${isNameInvalid ? classes.invalid : ''}`}
          type="text"
          placeholder="Имя поля"
          value={el.name}
          onChange={(e) => onChange(String(el.id), { name: e.target.value })}
        />
        <input
          className={`${dark ? classes.inputDark : classes.input} ${isDescriptionInvalid ? classes.invalid : ''}`}
          type="text"
          placeholder="Описание (подсказка)"
          value={el.description}
          onChange={(e) => onChange(String(el.id), { description: e.target.value })}
        />
        <select
          className={`${dark ? classes.selectDark : classes.select} ${isTypeInvalid ? classes.invalid : ''}`}
          value={el.type}
          onChange={(e) =>
            onChange(String(el.id), { type: Number(e.target.value) as CategoryFieldType })
          }
        >
          <option value={CategoryFieldType.NONE} disabled hidden>
            Выберите тип
          </option>
          {createTypeSelectList(CategoryFieldType, categoryFieldTypeToText)
            .filter((item) => Number(item.value) !== CategoryFieldType.NONE)
            .map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
        </select>
      </div>

      <div className={classes.toggles}>
        <div className={classes.toggleContainer}>
          <span className={classes.label}>Активно</span>
          <Toggle
            checked={el.isActive}
            onChange={() => onChange(String(el.id), { isActive: !el.isActive })}
          />
        </div>

        <div className={classes.toggleContainer}>
          <span className={classes.label}>Видно клиенту</span>
          <Toggle
            checked={el.isVisibleForClient}
            onChange={() => onChange(String(el.id), { isVisibleForClient: !el.isVisibleForClient })}
          />
        </div>

        <div className={classes.toggleContainer}>
          <span className={classes.label}>Заполняет клиент</span>
          <Toggle
            checked={el.isFillByClient}
            onChange={() => onChange(String(el.id), { isFillByClient: !el.isFillByClient })}
          />
        </div>

        <div className={classes.toggleContainer}>
          <span className={classes.label}>Обязательно</span>
          <Toggle
            checked={el.required}
            onChange={() => onChange(String(el.id), { required: !el.required })}
          />
        </div>
      </div>

      <div className={classes.buttons}>
        <IconButton
          alt="Добавить"
          icon={add}
          onClick={() => addItem(el)}
          display={length < maxFieldTemplatesCount}
          small
        />
        <IconButton
          alt="Наверх"
          icon={up}
          onClick={() => itemUp(el)}
          display={el.sortOrder !== sortStep && length > 1}
          small
        />
        <IconButton
          alt="Вниз"
          icon={down}
          onClick={() => itemDown(el)}
          display={length > 1 && index !== length - 1}
          small
        />
        <IconButton alt="Удалить" icon={trash} onClick={() => removeItem(el)} display small />
      </div>
    </div>
  )
}
